import React, { useCallback, useEffect, useState } from "react";
import {
  Typography,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  PlaceOutlined as LocationIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { Button } from "reactstrap";
import "./enable-location.scss";
import {
  fetchSuggestions,
  getCityCoordinates,
  getCityName,
} from "../../../api/mapbox";
import { useDispatch, useSelector } from "react-redux";
import { getTopThreeProviderAndPlanner } from "../../../features/home-page/search-helper";

export default function EnableLocation({
  openDialog,
  onClose,
  showCloseIcon = true,
}: {
  openDialog?: boolean;
  onClose: () => void;
  showCloseIcon?: boolean;
}) {
  // STATES
  const [openManualDialog, setOpenManualDialog] = useState<boolean>(false);
  const [manualCity, setManualCity] = useState<string>("");
  const [cityName, setCityName] = useState<string>("");
  const [cityError, setCityError] = useState<string>("");
  const [suggestions, setSuggestions] = useState<
    { main: string; secondary: string; fullPlaceName: string }[]
  >([]);
  const [isManualSelection, setIsManualSelection] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState<string>("");
  const [hasTyped, setHasTyped] = useState<boolean>(false);
  const dispatch = useDispatch();

  const { city } = useSelector((state: any) => state.userLocation);

  const handleEnableLocation = useCallback(() => {
    // if city already detected/set previously, open manual dialog directly
    if (cityName) {
      setOpenManualDialog(true);
      return;
    }

    // else fetching current location
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      setOpenManualDialog(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          const response = await getCityName(latitude, longitude);
          const address = response.data;
          const detectedCity = address.features[0]?.text;
          const place_name = address.features[0]?.place_name;

          if (detectedCity) {
            // set city & don't open modal here
            setCityName(detectedCity);

            //  Dispatch to Redux
            dispatch({
              type: "USER_LOCATION:SET_LOCATION",
              payload: {
                latitude,
                longitude,
                city: detectedCity,
              },
            });

            //  Save to localStorage
            localStorage.setItem(
              "user_location",
              JSON.stringify({
                latitude,
                longitude,
                city: detectedCity,
              })
            );
          } else {
            // if city not found, open manual entry modal
            setOpenManualDialog(true);
          }
        } catch (error) {
          console.error("Error in reverse geocoding:", error);
          // on any error, open manual modal
          setOpenManualDialog(true);
        }
      },
      (error) => {
        console.error("Geolocation error:", error.message);
        // User denied or failed — open manual modal
        setOpenManualDialog(true);
      }
    );
  }, [cityName, dispatch]);

  useEffect(() => {
    if (openDialog) {
      handleEnableLocation();
    }
  }, [openDialog, handleEnableLocation]);

  //   LOCATION HANDLERS
  const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
    setManualCity(value);
    setCityError("");
    setHasTyped(true); // mark that user typed
  };

  const handleSuggestionClick = async (suggestion: any) => {
    setManualCity(suggestion.fullPlaceName);
    setIsManualSelection(true);
    setSuggestions([]);
    setCityError("");
    setHasTyped(false); // reset typing flag
  };
  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const response = await getCityName(latitude, longitude);
          const address = response.data;
          const detectedCity = address.features[0]?.text;

          if (detectedCity) {
            // Fill the input field
            setManualCity(detectedCity);
            setCityError("");

            // update Redux and localStorage
            // dispatch({
            //   type: "USER_LOCATION:SET_LOCATION",
            //   payload: { latitude, longitude, city: detectedCity },
            // });
            // localStorage.setItem(
            //   "user_location",
            //   JSON.stringify({ latitude, longitude, city: detectedCity })
            // );
          } else {
            setCityError("Could not detect city. Please enter manually.");
          }
        } catch (error) {
          console.error("Error detecting city:", error);
          setCityError("Could not detect city. Please enter manually.");
        }
      },
      (error) => {
        console.error("Geolocation error:", error.message);
        setCityError("Could not detect city. Please enter manually.");
      }
    );
  };

  // DIALOG HANDLERS
  const closeManualDialog = () => {
    setOpenManualDialog(false);
    setSuggestions([]);
    setCityError("");
    setHasTyped(false);
    setManualCity(city || "");
    onClose();
  };

  const handleCitySubmit = async () => {
    if (!manualCity.trim()) {
      setCityError("City name is required.");
      return;
    }

    try {
      const response = await getCityCoordinates(manualCity);

      if (response.data.features?.length > 0) {
        const result = response.data.features[0];
        const [longitude, latitude] = result.center;
        const city = response.data.features[0].text;

        // Update Redux
        dispatch({
          type: "USER_LOCATION:SET_LOCATION",
          payload: {
            latitude,
            longitude,
            city: city,
          },
        });

        // Save to localStorage
        localStorage.setItem(
          "user_location",
          JSON.stringify({
            latitude,
            longitude,
            city: city,
          })
        );

        setCityName(manualCity);
        setCityError("");
        closeManualDialog();
        getTopThreeProviderAndPlanner(dispatch);
      } else {
        setCityError(
          "Sorry, we couldn’t find services in that area. Please try another city."
        );
      }
    } catch (error) {
      console.error("City validation failed:", error);
      setCityError(
        "Sorry, we couldn’t find services in that area. Please try another city."
      );
    }
    setHasTyped(false); // reset typing flag
  };

  // AUTOSUGGESTION
  useEffect(() => {
    const fetchCitySuggestions = async (query: string) => {
      try {
        const response = await fetchSuggestions(query);

        const cityNames = response.data.features.map((item: any) => {
          const city = item.text;

          // Extract state and country from context
          const region = item.context?.find((c: any) =>
            c.id.startsWith("region")
          )?.text;

          const country = item.context?.find((c: any) =>
            c.id.startsWith("country")
          )?.text;

          const secondaryText = [region, country].filter(Boolean).join(", ");

          return {
            main: city,
            secondary: secondaryText,
            fullPlaceName: item.place_name,
          };
        });

        setSuggestions(cityNames);
      } catch (error) {
        console.error("Failed to fetch city suggestions:", error);
      }
    };

    if (debouncedQuery) {
      fetchCitySuggestions(debouncedQuery);
    }
  }, [debouncedQuery]);

  // Inside your component
  useEffect(() => {
    const storedLocation = localStorage.getItem("user_location");
    if (storedLocation) {
      try {
        const parsed = JSON.parse(storedLocation);
        if (parsed.city) {
          setCityName(parsed.city);
        }
      } catch (e) {
        console.error("Error parsing stored location", e);
      }
    }
  }, []);

  // DEBOUNCE LOGIC
  useEffect(() => {
    if (!hasTyped) {
      setSuggestions([]);
      return;
    }
    if (isManualSelection) {
      setIsManualSelection(false);
      return;
    }
    if (!manualCity.trim()) {
      setSuggestions([]);
      return;
    }

    const handler = setTimeout(() => {
      if (manualCity.trim().length >= 2) {
        setDebouncedQuery(manualCity.trim());
      }
    }, 400);

    return () => {
      clearTimeout(handler);
    };
  }, [manualCity]);

  useEffect(() => {
    if (!city) {
      setCityName("");
    }
    setManualCity(cityName || city || "");
  }, [cityName, city]);

  return (
    <Dialog
      open={openManualDialog}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        closeManualDialog();
      }}
      PaperProps={{
        className: "custom-dialog-paper",
        onClick: (e: any) => e.stopPropagation(),
        sx: { borderRadius: "8px", padding: "0 1rem" },
      }}
      BackdropProps={{ className: "custom-dialog-backdrop" }}
    >
      <Box className="dialog-header">
        <Typography className="dialog-title">Location Access</Typography>
        {showCloseIcon && (
          <IconButton className="close-button" onClick={closeManualDialog}>
            <CloseIcon sx={{ color: "#174347" }} fontSize="medium" />
          </IconButton>
        )}
      </Box>

      <DialogContent className="dialog-content">
        <Typography className="dialog-description">
          Enter City name for location access.
        </Typography>

        <label htmlFor="manualCity" className="enable-location-input-label">
          Location<span className="required-asterisk">*</span>
        </label>
        <Box
          className="city-input-wrapper"
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(0, 0, 0, 0.23)",
            },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(0, 0, 0, 0.23)",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
            {
              borderWidth: "1px",
              borderColor: "rgba(0, 0, 0, 0.23)",
            },
          }}
        >
          <TextField
            id="manualCity"
            variant="outlined"
            fullWidth
            placeholder="Enter City Name"
            autoComplete="off"
            value={manualCity}
            onChange={handleCityChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleDetectLocation}
                    edge="end"
                    size="small"
                    aria-label="detect location"
                    sx={{
                      backgroundColor: "#BFA755",
                      color: "#fff",
                      borderRadius: "6px",
                      padding: "6px",
                      "&:hover": {
                        backgroundColor: "#a89246",
                      },
                    }}
                  >
                    <LocationIcon className="textfield-icon" fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            className="city-input"
          />

          {/* Error Message */}
          {cityError && (
            <Typography className="city-error-message" color="error">
              {cityError}
            </Typography>
          )}

          {/* Suggestions Dropdown */}
          {suggestions.length > 0 && (
            <Box className="suggestions-box">
              {suggestions.map((suggestion, index) => (
                <Box
                  key={index}
                  className="suggestion-item"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {suggestion.main}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#555" }}>
                    {suggestion.secondary}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Submit Button */}
        <DialogActions className="enable-location-dialog-actions">
          <Button
            className="submit-button"
            onClick={handleCitySubmit}
            disabled={manualCity.trim().length === 0}
          >
            OK
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}
